import React, { useEffect, useMemo } from "react"
import { Helmet } from "react-helmet"
import {
    Box,
    Container,
    Divider,
    FormControlLabel,
    Grid,
    InputAdornment,
    MenuItem,
    Paper,
    Popover,
    Select,
    TextField,
    Typography,
} from "@mui/material"
import Rating from '@mui/material/Rating'
import StarIcon from '@mui/icons-material/Star'
import Search from "@mui/icons-material/Search"
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip'
import { makeStyles } from "@mui/styles"
import { useTranslation } from "react-i18next"
import { Link as RouterLink, useNavigate, useLocation } from "react-router-dom"
import { styled } from "@mui/styles"

import cssVariables from "../../../theme/cssVariables"
import { ReactComponent as ChevronExpandDown } from "../../../assets/icons/chevron-expand-down.svg"
import { ReactComponent as ChevronExpandUp } from "../../../assets/icons/chevron-expand-up.svg"
import { ReactComponent as Nodata } from "../../../assets/icons/nodata.svg"
import * as Util from "../../../services/global.service"
import evaluateService from "../../../services/evaluate.service"

import CustomPagination from "../../../components/CustomPagination"
import CustomCheckbox from "../../../components/CustomCheckbox"

const useStyles = makeStyles({
    plusSvg: {
        "& > path": {
            fill: "#fff",
            stroke: "#fff",
        },
    },
    draggingListItem: {
        background: 'rgb(235,235,235)'
    },
    toggleBtnRoot: {
        background: `transparent !important`,
        color: "#6B6B6B !important",
        width: "320px",
        border: "none !important",
    },
    toggleBtnSelected: {
        background: `${cssVariables.color.primary}!important`,
        color: "#fff!important",
    },
    flexPaper: {
        flex: 1,
        minWidth: 350,
        "box-shadow": "0px 2px 10px rgba(0, 0, 0, 0.1) !important",
        "border-radius": "10px !important"
    },
    tab: {
        fontSize: "16px !important",
        fontWeight: "300 !important",
        // color: "#A3A3A3 !important",
        '&.Mui-selected': {
            color: cssVariables.color.primary + " !important"
        }
    },
    root: {
        display: 'flex',
        width: "100%",
        flexWrap: 'wrap'
    }
});
const styles = {
    link: {
        color: "#5DA196",
        cursor: "pointer",
    },
    end: {
        color: "#A3A3A3",
    },
}

const Ratings = styled(Rating)({
    '& .MuiRating-icon': {
        color: '#F7B254 !important',
    },
})

const employeeColumnWidths = [
    "30%",
    "24%",
    "0%",
    "20%",
    "13%",
    "13%",
]

const managerColumnWidths = [
    "30%",
    "18%",
    "14%",
    "14%",
    "12%",
    "12%",
]

const PreviousEvaluation = () => {
    const [list, setList] = React.useState([])
    const location = useLocation()
    const classes = useStyles()
    const { t, i18n } = useTranslation()
    const navigate = useNavigate()
    const [open, setOpen] = React.useState(false)
    const [columnWidths, setColumnWidths] = React.useState(employeeColumnWidths)
    const [search, setSearchValue] = React.useState("")
    const [perPage, setPerPage] = React.useState(10)
    const [currentPage, setCurrentPage] = React.useState(1)
    const [totalPage, setTotalPage] = React.useState(1)
    const [filterEmployeeList, setFilterEmployeeList] = React.useState([])
    const [filterEmployeeChecked, setFilterEmployeeChecked] = React.useState([])
    const [checkboxFiltersEmployeeStatus, setCheckboxFiltersEmployeeStatus] = React.useState("all")
    const [isManager, setIsManager] = React.useState(false)

    const handleFiltersEmployeeCheckbox = (data) => {
        let tmpFiltersCheckbox = JSON.parse(JSON.stringify(filterEmployeeChecked))
        let tmpFiltersCheckboxList = [...filterEmployeeList]
        if (data === "all") {
            if (checkboxFiltersEmployeeStatus === "indeterminate" || checkboxFiltersEmployeeStatus === "none") {
                let selectAllFilters = tmpFiltersCheckboxList.map(filter => filter)
                tmpFiltersCheckbox = selectAllFilters
            } else if (checkboxFiltersEmployeeStatus === "all") {
                tmpFiltersCheckbox = []
            }
        } else {
            let isExisted = tmpFiltersCheckbox.find(item => item === data)
            if (isExisted) {
                tmpFiltersCheckbox = tmpFiltersCheckbox.filter(item => item !== data)
            } else if (!isExisted) {
                tmpFiltersCheckbox.push(data)
            }
        }
        setFilterEmployeeChecked(tmpFiltersCheckbox)
    }

    const checkFilterEmployeeCheckbox = () => {
        let result = "none"
        if (filterEmployeeChecked.length === filterEmployeeList.length) {
            result = "all"
        } else if (filterEmployeeChecked.length > 0 && filterEmployeeChecked.length < filterEmployeeList.length) {
            result = "indeterminate"
        }
        setCheckboxFiltersEmployeeStatus(result)
    }

    const onScroll = (evt) => {
        if (evt !== undefined && evt !== null) {
            if (open) {
                setOpen(false)
            }
        }
    }

    const handleSearch = (event) => {
        setSearchValue(event.target.value)
    }

    const handleTotalPage = () => {
        const List = list
        const totalPage =
            Math.ceil(List.length / perPage) === 0
                ? 1
                : Math.ceil(List.length / perPage)
        setTotalPage(totalPage)
    }

    const clickLink = (data) => {
        navigate(data, { replace: false })
    }

    const handlePerPageChange = (event) => {
        setPerPage(event.target.value)
    }

    const handleSetCurrentPage = (value) => {
        const getSelectedPage = value.filter(item => item.type === "page" && item.selected)
        setCurrentPage(getSelectedPage.length > 0 ? getSelectedPage[0].page : 1)
    }

    const BootstrapTooltip = styled(({ className, ...props }) => (
        <Tooltip {...props} arrow classes={{ popper: className }} />
    ))(({ theme }) => ({
        [`& .${tooltipClasses.arrow}`]: {
            color: "#343A40",
        },
        [`& .${tooltipClasses.tooltip}`]: {
            backgroundColor: "#343A40",
            color: "#FFFFFF",
            padding: "11px 15px",
        },
    }))

    const sortData = (key, direction) => {
        let result = [...list]
        result.sort(
            (a, b) => {
                if (direction) {
                    return String(a[key]).localeCompare(String(b[key]))
                } else {
                    return String(b[key]).localeCompare(String(a[key]))
                }
            }
        )
        setList(result)
    }

    const currentData = useMemo(() => {
        const firstPageIndex = (currentPage - 1) * perPage
        const lastPageIndex = firstPageIndex + perPage
        return list
            .filter(row => filterEmployeeChecked.includes(row?.employee))
            .filter(row => Object.keys(row).some(key => row?.[key]?.toString().toLowerCase().indexOf(search.toString().toLowerCase()) !== -1))
            .slice(firstPageIndex, lastPageIndex)
    }, [list, currentPage, perPage, filterEmployeeChecked, search])

    // pop over
    const [anchorFilterEmployee, setAnchorFilterEmployee] = React.useState(null)
    const openFilterEmployee = Boolean(anchorFilterEmployee)
    const filterEmployee = openFilterEmployee ? 'filters-popover' : undefined
    const handleCloseFilterEmployee = () => {
        setAnchorFilterEmployee(null)
    }
    const handleClickEmployeeFilter = (event) => {
        setAnchorFilterEmployee(event.currentTarget)
    }

    // useEffect
    useEffect(() => {
        if (location.pathname !== "/kpi/performance-evaluation/previous") {
            evaluateService.getPreviousEvaluationList().then(
                (resp) => {
                    let data = resp?.data?.data
                    setList(data ?? [])
                    let tmpEmpList = []
                    data?.forEach(item => {
                        tmpEmpList.push(item?.employee)
                    })
                    tmpEmpList = [...new Set(tmpEmpList)]
                    setFilterEmployeeList(tmpEmpList)
                    setFilterEmployeeChecked(tmpEmpList)
                },
            )
        } else {
            evaluateService.getManagerPreviousEvaluationList().then(
                (resp) => {
                    let data = resp?.data?.data
                    setList(data ?? [])
                    let tmpEmpList = []
                    data?.forEach(item => {
                        tmpEmpList.push(item?.employee)
                    })
                    tmpEmpList = [...new Set(tmpEmpList)]
                    setFilterEmployeeList(tmpEmpList)
                    setFilterEmployeeChecked(tmpEmpList)
                },
            )
        }
    }, [])
    useEffect(() => {
        handleTotalPage()
    }, [list, perPage])
    useEffect(() => {
        checkFilterEmployeeCheckbox()
    }, [filterEmployeeChecked])

    useEffect(() => {
        if (location.pathname === "/kpi/performance-evaluation/previous") {
            setIsManager(true)
        } else {
            setIsManager(false)
        }
    }, [location.pathname])

    useEffect(() => {
        if (isManager) {
            setColumnWidths(managerColumnWidths)
        } else {
            setColumnWidths(employeeColumnWidths)
        }
    }, [isManager])

    return (
        <Box onScroll={onScroll} style={{ maxHeight: "100%", overflowY: "auto" }}>
            <Helmet>
                <title>{!isManager ? "My Evaluation" : "Manage Evaluation"} | PMS </title>
            </Helmet>
            <Box
                sx={{
                    backgroundColor: 'background.default',
                    minHeight: '100%',
                    py: 3
                }}
            >
                <Container maxWidth="xl">
                    <Grid container pt={2} pb={2}>
                        <Typography fontSize={16} lineHeight={1} color="#68717A">
                            <span style={styles.link} onClick={() => clickLink('/kpi/home')}>{t('label.home')}</span>
                            <span style={styles.link} onClick={() => clickLink(isManager ? "/kpi/performance-evaluation" : "/kpi/evaluation")}>{' / '}{isManager ? t("sidebar.label.evaluationManage") : t("sidebar.label.evaluation")}</span>
                            <span style={styles.end} >{` / ${t('evaluation.previousEvaluation')}`}</span>
                        </Typography>
                    </Grid>
                    <Grid container item >
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }} pt="7px" pb="30px">
                            <Typography variant="h2" color="#404349" fontSize={36} lineHeight="40px" >
                                {t('evaluation.previousEvaluation')}
                            </Typography>
                            <Box >
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                                    <TextField
                                        id="input-with-icon-textfield"
                                        fullWidth
                                        placeholder={t('label.search')}
                                        size="small"
                                        sx={{ background: "#fff", width: "400px", "input": { padding: "11px 14px" } }}
                                        className="searchTextField-h40"
                                        value={search}
                                        onChange={handleSearch}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Search style={{ color: "#C4C4C4" }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid container spacing={2}>
                        <Grid container item >
                            <div className={classes.root}>
                                <Paper className={classes.flexPaper} >
                                    <Box
                                        px="32px"
                                    >
                                        <Box
                                            px="25px"
                                            py="19px"
                                            sx={{ display: "flex", flexDirection: "row" }}
                                        >
                                            <Box sx={{ display: "flex", flexDirection: "row", width: columnWidths[0] }} pr="15px">
                                                <Typography fontSize={16} lineHeight="23.96px" color="#A0A0A0" fontWeight="300px" sx={{ whiteSpace: "nowrap", width: "100%", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "max-content", mr: "10px" }}>
                                                    {t("evaluation.formName")}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: "flex", flexDirection: "row", width: columnWidths[1] }} pr="15px">
                                                <Typography fontSize={16} lineHeight="23.96px" color="#A0A0A0" fontWeight="300px" sx={{ whiteSpace: "nowrap", width: "100%", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "max-content", mr: "10px" }}>
                                                    {t('evaluation.formType')}
                                                </Typography>
                                            </Box>
                                            {columnWidths[2] !== "0%" &&
                                                <Box sx={{ display: "flex", flexDirection: "row", width: columnWidths[2] }} pr="15px">
                                                    <Typography fontSize={16} lineHeight="23.96px" color="#A0A0A0" fontWeight="300px" sx={{ whiteSpace: "nowrap", width: "100%", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "max-content", mr: "10px" }}>
                                                        {t('evaluation.employee')}
                                                    </Typography>
                                                    <Typography component="span" style={{ flexDirection: "column", justifyContent: "center", display: "flex" }}>
                                                        <ChevronExpandDown style={{ cursor: "pointer" }} onClick={e => handleClickEmployeeFilter(e)} />
                                                    </Typography>
                                                    <Popover
                                                        id={filterEmployee}
                                                        open={openFilterEmployee}
                                                        anchorEl={anchorFilterEmployee}
                                                        onClose={handleCloseFilterEmployee}
                                                        anchorOrigin={{
                                                            vertical: 'bottom',
                                                            horizontal: 'left',
                                                        }}
                                                    >
                                                        {filterEmployeeList ?
                                                            (<Box
                                                                sx={{
                                                                    p: 2,
                                                                }}>
                                                                <Typography fontSize="16px" fontWeight="600" sx={{ my: 1 }}>
                                                                    {t("label.employee")}
                                                                </Typography>
                                                                <Box
                                                                    sx={{
                                                                        display: "flex",
                                                                        alignItems: "flex-start",
                                                                        flexDirection: "column",
                                                                        pl: "4px",
                                                                        pr: "4px"
                                                                    }}>
                                                                    <FormControlLabel
                                                                        style={{ fontSize: '14px' }}
                                                                        label={t("label.selectAll")}
                                                                        control={
                                                                            <CustomCheckbox
                                                                                checked={checkboxFiltersEmployeeStatus === 'all'}
                                                                                indeterminate={checkboxFiltersEmployeeStatus === 'indeterminate'}
                                                                                onChange={() => handleFiltersEmployeeCheckbox("all")}
                                                                            />
                                                                        }
                                                                    />
                                                                    {
                                                                        filterEmployeeList.map((item, index) => {
                                                                            return (
                                                                                <FormControlLabel
                                                                                    style={{ fontSize: '14px' }}
                                                                                    label={item}
                                                                                    key={index}
                                                                                    control={<CustomCheckbox color="primary" checked={filterEmployeeChecked.includes(item)} onChange={() => handleFiltersEmployeeCheckbox(item)} />}
                                                                                />
                                                                            )
                                                                        })
                                                                    }
                                                                </Box>
                                                            </Box>) : null
                                                        }
                                                    </Popover>
                                                </Box>
                                            }
                                            <Box sx={{ display: "flex", flexDirection: "row", width: columnWidths[3] }} pr="15px">
                                                <Typography fontSize={16} lineHeight="23.96px" color="#A0A0A0" fontWeight="300px" sx={{ whiteSpace: "nowrap", width: "100%", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "max-content", mr: "10px" }}>
                                                    {t('evaluation.evaluator')}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: "flex", flexDirection: "row", width: columnWidths[4] }} pr="15px">
                                                <Typography fontSize={16} lineHeight="23.96px" color="#A0A0A0" fontWeight="300px" sx={{ whiteSpace: "nowrap", width: "100%", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "max-content", mr: "10px" }}>
                                                    {t('evaluation.evaluatedDate')}
                                                </Typography>
                                                <Typography component="span" style={{ flexDirection: "column", justifyContent: "center", display: "flex" }}>
                                                    <ChevronExpandUp style={{ cursor: "pointer" }} onClick={e => sortData("evaluate_date", true)} />
                                                    <ChevronExpandDown style={{ cursor: "pointer" }} onClick={e => sortData("evaluate_date", false)} />
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: "flex", flexDirection: "row", width: columnWidths[5] }} pr="15px">
                                                <Typography fontSize={16} lineHeight="23.96px" color="#A0A0A0" fontWeight="300px" sx={{ whiteSpace: "nowrap", width: "100%", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "max-content", mr: "10px" }}>
                                                    {t('evaluation.rating')}
                                                </Typography>
                                                <Typography component="span" style={{ flexDirection: "column", justifyContent: "center", display: "flex" }}>
                                                    <ChevronExpandUp style={{ cursor: "pointer" }} onClick={e => sortData("evaluator_rating", true)} />
                                                    <ChevronExpandDown style={{ cursor: "pointer" }} onClick={e => sortData("evaluator_rating", false)} />
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Divider width="100%" />
                                    </Box>
                                    {currentData && currentData.length > 0 ?
                                        currentData.map((row, index) => (
                                            <Box px="32px" >
                                                <Box sx={{ width: "100%", position: "relative", transition: "0.5s" }} key={index} >
                                                    <Box
                                                        sx={{ minHeight: "96px", background: "#FFF", width: "100%", display: "flex", alignItems: "center" }}
                                                        px="25px" py="20px"
                                                    >
                                                        <Box sx={{ width: columnWidths[0] }} pr="15px" >
                                                            <Box
                                                                display="flex"
                                                                sx={{
                                                                    flexFlow: "column",
                                                                    textAlign: "left",
                                                                    // marginLeft: 2,
                                                                    position: "relative",
                                                                }}
                                                            >
                                                                <BootstrapTooltip title={Util.formatData(row?.form_name ?? "", "")} placement="top">
                                                                    <Typography
                                                                        fontWeight={600}
                                                                        fontSize={18}
                                                                        lineHeight="27px"
                                                                        color="#404349"
                                                                        sx={{ whiteSpace: "nowrap", width: "100%", overflow: "hidden", textOverflow: "ellipsis" }}
                                                                    >
                                                                        {Util.formatData(row?.form_name ?? "", "")}
                                                                    </Typography>
                                                                </BootstrapTooltip>
                                                                {
                                                                    Util.formatData(row?.position ?? "", false) ?
                                                                        <Typography lineHeight="23.92px" fontSize={16} color="#A3A3A3" fontWeight={400} sx={{ whiteSpace: "nowrap", width: "100%", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                                            {row?.position ?? ""}
                                                                        </Typography>
                                                                        :
                                                                        null
                                                                }
                                                                {
                                                                    Util.formatData(row?.organization ?? "", false) ?
                                                                        <Typography lineHeight="23.92px" fontSize={16} color="#A3A3A3" fontWeight={400} sx={{ whiteSpace: "nowrap", width: "100%", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                                            ({row?.organization ?? ""})
                                                                        </Typography>
                                                                        :
                                                                        null
                                                                }
                                                            </Box>
                                                        </Box>
                                                        <Box sx={{ width: columnWidths[1] }} pr="15px">
                                                            <Typography fontWeight="400" fontSize={18} lineHeight="23.92px" sx={{ whiteSpace: "nowrap", width: "100%", overflow: "hidden", textOverflow: "ellipsis" }} color="#404349">
                                                                {Util.formatData(row?.form_type ?? "", "")}
                                                            </Typography>
                                                            <Typography fontSize={14} fontWeight={400} color="#A3A3A3" lineHeight="27px">
                                                                {Util.dateMonthFormatAdmin(row?.start_at)} - {Util.dateMonthFormatAdmin(row?.end_at)}
                                                            </Typography>
                                                        </Box>
                                                        {columnWidths[2] !== "0%" &&
                                                            <Box sx={{ width: columnWidths[2], display: "flex", justifyContent: "flex-start", alignItems: "center" }} pr="15px">
                                                                <Box ml={0} sx={{ width: "100%" }}>
                                                                    <Typography fontWeight="400" fontSize={18} lineHeight="26.91px" sx={{ color: "#404349", whiteSpace: "break-spaces", width: "100%", overflow: "hidden", textOverflow: "ellipsis" }} color="#404349">
                                                                        {Util.splitString(row?.employee ?? "")?.[0]}
                                                                    </Typography>
                                                                    <Typography fontWeight="400" fontSize={18} lineHeight="26.91px" sx={{ color: "#404349", whiteSpace: "break-spaces", width: "100%", overflow: "hidden", textOverflow: "ellipsis" }} color="#404349">
                                                                        {Util.splitString(row?.employee ?? "")?.[1]}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        }
                                                        <Box sx={{ width: columnWidths[3], display: "flex", justifyContent: "flex-start", alignItems: "center" }} pr="15px">
                                                            <Box ml={0} sx={{ width: "100%" }}>
                                                                <Typography fontWeight="400" fontSize={18} lineHeight="26.91px" sx={{ color: "#404349", whiteSpace: "break-spaces", width: "100%", overflow: "hidden", textOverflow: "ellipsis" }} color="#404349">
                                                                    {Util.splitString(row?.evaluator ?? "")?.[0]}
                                                                </Typography>
                                                                <Typography fontWeight="400" fontSize={18} lineHeight="26.91px" sx={{ color: "#404349", whiteSpace: "break-spaces", width: "100%", overflow: "hidden", textOverflow: "ellipsis" }} color="#404349">
                                                                    {Util.splitString(row?.evaluator ?? "")?.[1]}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                        <Box sx={{ display: "flex", flexDirection: "column", width: columnWidths[4] }} pr="15px">
                                                            <Typography fontWeight="400" fontSize={16} color="#404349" lineHeight="24px" sx={{ color: "#404349", whiteSpace: "nowrap", width: "100%", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                                {Util.dateDayFormatAdmin(row?.evaluate_date, "")}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ display: "flex", flexDirection: "column", width: columnWidths[5] }} pr="15px">
                                                            {
                                                                row?.evaluator_rating && row?.evaluator_rating !== "" ?
                                                                    row?.score_type === "rating" ?
                                                                        <Box sx={{ display: "flex", alignItems: "center", pl: "5px" }}>
                                                                            <Ratings fontSize={19} readOnly name="text-feedback" emptyIcon={<StarIcon fontSize="inherit" />} defaultValue={Util.formatData(Number(row?.evaluator_rating).toFixed(1), 0)} max={1} />
                                                                            <Typography fontSize={16} pl="5px" pr="5px" fontWeight="500" color="#404349">
                                                                                {row?.evaluator_rating}
                                                                            </Typography>
                                                                        </Box>
                                                                        :
                                                                        <Box sx={{ display: "flex", alignItems: "center", pl: "5px" }}>
                                                                            <Typography fontSize={16} pl="5px" pr="5px" fontWeight="500" color="#404349">
                                                                                {Number(Util.formatData(row?.evaluator_rating, "0")).toFixed(2) ?? "-"}%
                                                                            </Typography>
                                                                        </Box>

                                                                    :
                                                                    <Box sx={{ display: "flex", alignItems: "center", pl: "5px" }}>
                                                                        <Rating fontSize={19} readOnly name="text-feedback" emptyIcon={<StarIcon fontSize="inherit" />} defaultValue={Util.formatData(Number(row?.evaluator_rating).toFixed(1), 0)} max={1} />
                                                                        <Typography fontSize={18} pl="5px" pr="5px" fontWeight="400">
                                                                            {"-"}
                                                                        </Typography>
                                                                    </Box>
                                                            }
                                                        </Box>
                                                    </Box>
                                                    {row?.remark !== undefined && row?.remark !== "" ?
                                                        <Box ml="25px" mb="25px" px="10px" py="5px" sx={{ background: "#EFF6F4", "border-radius": "5px" }}>
                                                            <Typography color="#404349" fontWeight="700" fontSize={14} lineHeight="20.93px" >
                                                                {t('label.remark')} : <span style={{ fontWeight: "400" }}>{row?.remark}</span>
                                                            </Typography>
                                                        </Box>
                                                        : null
                                                    }
                                                </Box>
                                                <Divider width="100%" />
                                            </Box>
                                        ))
                                        :
                                        <Box sx={{ pt: "50px", textAlign: "center", pb: "500px" }}>
                                            <Nodata />
                                            <Typography color="#A0A0A0" fontWeight="500" fontSize={20} lineHeight="30px" >
                                                {t('label.noData')}
                                            </Typography>
                                        </Box>
                                    }
                                </Paper>
                            </div>
                        </Grid>
                    </Grid>
                    <Grid container mt={1} spacing={2} >
                        <Grid item xs sm md display="flex" alignItems="center">
                            <Box mr={1}>
                                <Typography variant="s14_fw400">{t('label.showMaxItems')}</Typography>
                            </Box>
                            <Select
                                id="pagination"
                                variant="outlined"
                                defaultValue={10}
                                value={perPage}
                                onChange={handlePerPageChange}
                                displayEmpty
                                size="small"
                                className="white-bg"
                            >
                                <MenuItem value={10}>
                                    <Typography style={{ fontWeight: "300" }}>10</Typography>
                                </MenuItem>
                                <MenuItem value={20}>
                                    <Typography style={{ fontWeight: "300" }}>20</Typography>
                                </MenuItem>
                                <MenuItem value={30}>
                                    <Typography style={{ fontWeight: "300" }}>30</Typography>
                                </MenuItem>
                            </Select>
                        </Grid>
                        <Grid item display="flex" alignItems="center" ml="auto">
                            <Typography variant="s14_fw400">
                                {t('label.page')} {currentPage} {t('label.of')} {totalPage}
                            </Typography>
                        </Grid>
                        <Grid item ml="auto" display="flex" alignItems="center">
                            <CustomPagination
                                setCurrentPage={handleSetCurrentPage}
                                selected={currentPage}
                                count={totalPage}
                            ></CustomPagination>
                        </Grid>
                    </Grid>
                </Container>
            </Box>
        </Box >
    );
};
export default PreviousEvaluation;
